import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { currency, compactCurrency, num, dateTime } from "@/lib/format";
import { StatusBadge, SkuTag } from "@/components/Badges";
import { PageHeader } from "@/components/Layout";
import { DollarSign, Layers, AlertTriangle, Flame, ArrowUpRight, ArrowDownRight, TrendingUp, ShoppingCart, Upload } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";

const KPICard = ({ testid, label, value, sub, icon: Icon, accent, tone }) => (
  <div data-testid={testid} className="relative overflow-hidden rounded-xl bg-white border border-stone-200/80 p-5 shadow-sm hover:border-stone-300 transition-colors animate-fade-up">
    <div className="absolute top-0 left-0 right-0 h-1" style={{ background: accent }} />
    <div className="flex items-start justify-between">
      <div className="text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</div>
      <div className="p-2 rounded-lg bg-stone-100 text-stone-700"><Icon className="h-4 w-4" /></div>
    </div>
    <div className="mt-3 text-3xl font-extrabold tracking-tight font-mono text-stone-900">{value}</div>
    {sub && <div className={`mt-1 text-xs font-medium ${tone || "text-stone-500"}`}>{sub}</div>}
  </div>
);

const MOVE_ICON = {
  receipt: { i: ArrowDownRight, c: "text-emerald-600 bg-emerald-50" },
  production: { i: Flame, c: "text-violet-600 bg-violet-50" },
  adjustment: { i: TrendingUp, c: "text-amber-600 bg-amber-50" },
  transfer: { i: ArrowUpRight, c: "text-blue-600 bg-blue-50" },
  order: { i: ShoppingCart, c: "text-rose-600 bg-rose-50" },
  import: { i: Upload, c: "text-stone-600 bg-stone-100" },
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="p-8 text-stone-400">Loading dashboard…</div>;

  const chartData = d.facility_values.map((f) => ({ name: f.name.replace(/'s.*| HQ.*| 3PL/, "").trim(), value: f.value, accent: f.accent, id: f.id }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader title="Executive Dashboard" subtitle="Live inventory position across all facilities — Fun Guy functional mushroom confections" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard testid="kpi-total-inventory-value" label="Total Inventory Value" value={compactCurrency(d.total_value)} sub={currency(d.total_value)} icon={DollarSign} accent="#78350F" />
        <KPICard testid="kpi-active-skus" label="Active SKUs Tracked" value={num(d.active_skus)} sub={`${d.category_count} categories`} icon={Layers} accent="#2E4F4F" />
        <KPICard testid="kpi-low-stock-alerts" label="Low Stock Warnings" value={num(d.low_stock_count)} sub={`${d.out_of_stock_count} out of stock`} icon={AlertTriangle} accent="#F59E0B" tone="text-amber-700" />
        <KPICard testid="kpi-active-production" label="Batches In Production" value={num(d.active_run_count)} sub={`${num(d.queued_units)} units queued`} icon={Flame} accent="#8B5CF6" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* facility value chart */}
        <div className="lg:col-span-7 rounded-xl bg-white border border-stone-200/80 shadow-sm p-5">
          <h2 className="text-lg font-semibold tracking-tight text-stone-800 mb-1">Inventory Value by Facility</h2>
          <p className="text-xs text-stone-500 mb-4">Click a bar to drill into a facility</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#78716C" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => compactCurrency(v)} tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} width={60} />
              <Tooltip formatter={(v) => currency(v)} cursor={{ fill: "#F5F4F0" }} contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E0", fontSize: 12 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(e) => navigate(`/facilities/${e.id}`)}>
                {chartData.map((c, i) => <Cell key={i} fill={c.accent} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* active production */}
        <div className="lg:col-span-5 rounded-xl bg-white border border-stone-200/80 shadow-sm p-5">
          <h2 className="text-lg font-semibold tracking-tight text-stone-800 mb-4">Active Production</h2>
          <div className="space-y-3">
            {d.active_runs.length === 0 && <p className="text-sm text-stone-400">No active runs.</p>}
            {d.active_runs.map((r) => (
              <div key={r.batch_no} className="p-3 rounded-lg border border-stone-100 bg-stone-50/50" data-testid={`dash-run-${r.batch_no}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-stone-800">{r.output_name}</span>
                  <SkuTag code={r.batch_no} />
                </div>
                <div className="text-xs text-stone-500 mt-0.5">{r.facility_name} · {r.scheduled_date}</div>
                <div className="mt-2 h-1.5 rounded-full bg-stone-200 overflow-hidden">
                  <div className="h-full bg-violet-500 rounded-full" style={{ width: `${r.yield_pct}%` }} />
                </div>
                <div className="text-[11px] text-stone-500 mt-1 font-mono">{num(r.completed_qty)} / {num(r.target_qty)} units ({r.yield_pct}%)</div>
              </div>
            ))}
          </div>
        </div>

        {/* low stock */}
        <div className="lg:col-span-7 rounded-xl bg-white border border-stone-200/80 shadow-sm overflow-hidden">
          <div className="p-5 pb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h2 className="text-lg font-semibold tracking-tight text-stone-800">Reorder Action List</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-y border-stone-200">
                  <th className="px-5 py-2.5 text-left">Item</th>
                  <th className="px-5 py-2.5 text-right">On Hand</th>
                  <th className="px-5 py-2.5 text-right">Reorder Pt</th>
                  <th className="px-5 py-2.5 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {d.low_stock_items.map((s) => (
                  <tr key={s.sku_code} onClick={() => navigate(`/skus/${s.sku_code}`)} className="border-b border-stone-100 hover:bg-stone-50/70 cursor-pointer" data-testid={`dash-low-${s.sku_code}`}>
                    <td className="px-5 py-3"><div className="font-medium text-stone-800">{s.name}</div><SkuTag code={s.sku_code} /></td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-stone-700">{num(s.total_on_hand)} {s.unit}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-stone-500">{num(s.reorder_point)}</td>
                    <td className="px-5 py-3"><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* recent movements */}
        <div className="lg:col-span-5 rounded-xl bg-white border border-stone-200/80 shadow-sm p-5">
          <h2 className="text-lg font-semibold tracking-tight text-stone-800 mb-4">Recent Movements</h2>
          <div className="space-y-2.5">
            {d.recent_movements.map((m, i) => {
              const conf = MOVE_ICON[m.type] || MOVE_ICON.transfer;
              const Icon = conf.i;
              return (
                <div key={i} className="flex items-start gap-3" data-testid={`dash-move-${i}`}>
                  <div className={`p-1.5 rounded-md ${conf.c} shrink-0 mt-0.5`}><Icon className="h-3.5 w-3.5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-stone-800 font-medium truncate">{m.sku_name}</div>
                    <div className="text-xs text-stone-500">{m.facility_name} · {dateTime(m.date)}</div>
                  </div>
                  <div className={`text-sm font-mono font-semibold shrink-0 ${m.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {m.delta >= 0 ? "+" : ""}{num(m.delta)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
